<template>
  <div class="mc-server-page">
    <div class="title">多人游戏</div>
    
    <div class="server-container">
      <div class="server-list">
        <div v-if="loading" class="loading-container">
          <div class="loading-spinner"></div>
          <div class="loading-text">正在加载服务器数据...</div>
        </div>
        <div v-else-if="error" class="error-container">
          <div class="error-text">{{ error }}</div>
          <button class="mc-button retry-button" @click="fetchServerData">重试</button>
        </div>
        <div v-else class="server-entry" 
             v-for="server in sortedServers" 
             :key="server.id" 
             :class="{ 'selected': selectedServer === server.id }"
             @click="selectServer(server.id)">
          <div class="server-icon">
            <img src="../assets/serverLogo.png" alt="Server Icon">
          </div>
          <div class="server-info">
            <div class="server-name">{{ server.name }}</div>
            <div class="server-status-container">
              <div class="server-status" v-tooltip="generatePlayersList(server)">
                <span class="status-text">在线</span> - <span class="player-count">{{ server.players_online }}/{{ server.players_max }}</span> <span class="player-text">玩家</span>
              </div>
              <div class="ping-indicator" v-tooltip="'延迟: ' + formatLatency(server.latency)">
                <div class="signal-bar signal-excellent"></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    
    <div class="button-container">
      <div class="button-row">
        <button class="mc-button" @click="joinServer(getSelectedServer())" :disabled="!selectedServer">加入服务器</button>
        <button class="mc-button" @click="showDirectConnect = true">直接连接</button>
        <button class="mc-button" @click="showAddServer = true">添加服务器</button>
      </div>
      
      <div class="button-row">
        <button class="mc-button" @click="showEditServer = true" :disabled="!selectedServer">编辑</button>
        <button class="mc-button" @click="deleteServer" :disabled="!selectedServer">删除</button>
        <button class="mc-button" @click="refreshServers">刷新</button>
        <button class="mc-button" @click="$emit('back')">取消</button>
      </div>
    </div>

    <!-- Direct connect dialog could be added here -->
  </div>
</template>

<script>
import axios from 'axios';
import config from '../config';

export default {
  name: 'McServerList',
  data() {
    return {
      selectedServer: null,
      servers: [],
      loading: true,
      error: null,
      showDirectConnect: false,
      showAddServer: false,
      showEditServer: false
    }
  },
  computed: {
    sortedServers() {
      return [...this.servers].sort((a, b) => b.players_online - a.players_online);
    }
  },
  created() {
    this.fetchServerData();
  },
  methods: {
    fetchServerData() {
      this.loading = true;
      const url = `${config.apiBaseUrl}${config.serverDataEndpoint}`;
      console.log('Fetching server data from:', url);
      
      axios.get(url)
        .then(response => {
          console.log('Server data received:', response.data);
          this.servers = response.data;
          this.loading = false;
          if (this.servers.length > 0 && !this.selectedServer) {
            this.selectedServer = this.servers[0].id;
          }
        })
        .catch(error => {
          console.error('Error fetching server data:', error);
          this.error = 'Failed to load server data';
          this.loading = false;
          // We won't load mock data anymore since we want to use the real API
          alert('无法连接到服务器API: ' + url);
        });
    },
    selectServer(id) {
      this.selectedServer = id;
    },
    getSelectedServer() {
      return this.servers.find(server => server.id === this.selectedServer) || null;
    },
    joinServer(server) {
      if (server) {
        alert(`连接到服务器: ${server.name} (${server.host}:${server.port})`);
        // In a real implementation, this would connect to the Minecraft server
      }
    },
    refreshServers() {
      this.loading = true;
      this.fetchServerData();
    },
    deleteServer() {
      if (confirm('确定要删除这个服务器吗?')) {
        // In a real implementation, this would send a delete request to the API
        // For now, we'll just remove it from the local array
        const index = this.servers.findIndex(s => s.id === this.selectedServer);
        if (index !== -1) {
          this.servers.splice(index, 1);
          if (this.servers.length > 0) {
            this.selectedServer = this.servers[0].id;
          } else {
            this.selectedServer = null;
          }
        }
      }
    },
    generatePlayersList(server) {
      if (!server.players || server.players.length === 0) {
        return '没有玩家在线';
      }
      return '在线玩家:\n' + server.players.join('\n');
    },
    formatLatency(latency) {
      return `${Math.round(latency)}ms`;
    }
  },
  directives: {
    tooltip: {
      bind(el, binding) {
        el.setAttribute('title', binding.value);
      },
      update(el, binding) {
        el.setAttribute('title', binding.value);
      }
    }
  }
}
</script>

<style scoped>
.mc-server-page {
  width: 100%;
  height: 100vh;
  background-image: url('../assets/GameBackgroud.png');
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  color: white;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 0;
  box-sizing: border-box;
}

.title {
  font-size: 24px;
  margin-top: 5px;
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.7);
  text-align: center;
}

.server-container {
  width: 100%;
  display: flex;
  justify-content: center;
  margin-top: 60px;
  height: calc(100vh - 300px); /* 为标题和按钮留出空间 */
}

.server-list {
  width: 80%;
  max-width: 800px;
  background-color: rgba(0, 0, 0, 0.8);
  border: 1px solid #444;
  padding: 0;
  overflow-y: auto;
  overflow-x: hidden;
  height: 100%; /* 使用容器的完整高度 */
  /* 添加触屏滚动支持 */
  -webkit-overflow-scrolling: touch;
  /* 自定义滚动条样式 */
  scrollbar-width: thin;
  scrollbar-color: #666 #333;
}

/* Webkit滚动条样式 */
.server-list::-webkit-scrollbar {
  width: 8px;
}

.server-list::-webkit-scrollbar-track {
  background: #333;
}

.server-list::-webkit-scrollbar-thumb {
  background: #666;
  border-radius: 4px;
}

.server-list::-webkit-scrollbar-thumb:hover {
  background: #888;
}

.server-entry {
  display: flex;
  align-items: center;
  padding: 10px;
  border-bottom: 1px solid #444;
  cursor: pointer;
  height: 70px;
}

.server-entry:last-child {
  border-bottom: none;
}

.server-entry.selected {
  background-color: rgba(255, 255, 255, 0.1);
}

.server-icon {
  width: 64px;
  height: 64px;
  margin-right: 15px;
  overflow: hidden;
}

.server-icon img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.server-info {
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.server-name {
  font-size: 18px;
  margin-bottom: 5px;
  color: #ffff55; /* Minecraft yellow */
  text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.7);
}

.server-status-container {
  display: flex;
  align-items: center;
}

.server-status {
  font-size: 14px;
  color: #aaaaaa;
  margin-right: 10px;
  cursor: help;
}

.status-text {
  color: #55ff55; /* Minecraft green */
}

.player-count {
  color: #5555ff; /* Minecraft blue */
}

.player-text {
  color: #aaaaaa;
}

.ping-indicator {
  display: flex;
  align-items: center;
  cursor: help;
}

.signal-bar {
  width: 12px;
  height: 12px;
  border-radius: 50%;
}

.signal-excellent {
  background-color: #43b581; /* Green */
}

.signal-good {
  background-color: #faa61a; /* Yellow */
}

.signal-fair {
  background-color: #f04747; /* Red */
}

.signal-poor {
  background-color: #747f8d; /* Gray */
}

.button-container {
  margin-top: 200px;
  padding-bottom: 0px;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  position: absolute;
  bottom: 0;
}

.button-row {
  display: flex;
  justify-content: center;
  margin-bottom: 8px;
  width: 80%;
  max-width: 800px;
}

.mc-button {
  background-color: rgba(60, 60, 60, 0.8);
  border: 2px solid #333333;
  color: white;
  padding: 8px 16px;
  margin: 0 4px;
  cursor: pointer;
  font-size: 16px;
  min-width: 140px;
  transition: background-color 0.2s;
}

.mc-button:hover {
  background-color: rgba(80, 80, 80, 0.8);
}

.loading-container, .error-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 200px;
  width: 100%;
}

.loading-spinner {
  width: 40px;
  height: 40px;
  border: 4px solid rgba(255, 255, 255, 0.2);
  border-radius: 50%;
  border-top-color: #ffff55;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.loading-text, .error-text {
  color: #aaaaaa;
  margin-top: 16px;
  font-size: 16px;
}

.error-text {
  color: #f04747;
}

.retry-button {
  margin-top: 16px;
  background-color: #4d4d4d;
}
</style> 